/**
 * Upload Screen Component
 *
 * Initial screen for template build - allows PDF upload or text paste.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  CloudUpload as UploadIcon,
  Description as DocIcon,
  TextSnippet as TextIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useFileUpload } from '../hooks/useFileUpload';
import type { UploadMode } from '../types';

interface UploadScreenProps {
  /** Optional callback after successful upload */
  onUploadSuccess?: (taskId: string) => void;
}

/**
 * Upload Screen - Initial screen for starting a template build
 */
export const UploadScreen: React.FC<UploadScreenProps> = ({ onUploadSuccess }) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { templateBuild } = useEventTracking();

  const [mode, setMode] = useState<UploadMode>('pdf');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const { uploadFile, uploadPastedText, isUploading, error, clearError } = useFileUpload();

  const handleModeChange = (_event: React.SyntheticEvent, newValue: UploadMode) => {
    setMode(newValue);
    clearError();
  };

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    clearError();
  }, [clearError]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file && file.type === 'application/pdf') {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSubmit = useCallback(async () => {
    // Track upload type
    templateBuild.uploadType({
      upload_type: mode,
      file_size_kb: mode === 'pdf' && selectedFile ? selectedFile.size / 1024 : undefined,
    });

    let result;
    if (mode === 'pdf' && selectedFile) {
      result = await uploadFile(selectedFile);
    } else if (mode === 'text' && pastedText) {
      result = await uploadPastedText(pastedText);
    }

    if (result?.task_id) {
      // Track build started
      templateBuild.started({ task_id: result.task_id });

      if (onUploadSuccess) {
        onUploadSuccess(result.task_id);
      }
    }
  }, [mode, selectedFile, pastedText, uploadFile, uploadPastedText, onUploadSuccess, templateBuild]);

  const canSubmit = mode === 'pdf' ? !!selectedFile : pastedText.trim().length >= 50;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 600,
          p: 4,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        {/* Title */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
          Create a Template
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: 'center' }}
        >
          Upload a PDF or paste text from an existing template to get started
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Mode Tabs */}
        <Tabs
          value={mode}
          onChange={handleModeChange}
          variant="fullWidth"
          sx={{ mb: 3, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Tab
            value="pdf"
            label="Upload PDF"
            icon={<DocIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            value="text"
            label="Paste Text"
            icon={<TextIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>

        {/* PDF Upload Mode */}
        {mode === 'pdf' && (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: isDragging
                ? theme.palette.primary.main + '08'
                : theme.palette.background.default,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
            onClick={handleBrowseClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            {selectedFile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <DocIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  Remove
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <UploadIcon sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Drop a PDF here or click to browse
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Maximum file size: 10 MB
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Text Paste Mode */}
        {mode === 'text' && (
          <Box>
            <TextField
              multiline
              rows={8}
              fullWidth
              placeholder="Paste the text content from your existing template here..."
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.background.default,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {pastedText.length} characters
              {pastedText.length < 50 && ' (minimum 50 required)'}
            </Typography>
          </Box>
        )}

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!canSubmit || isUploading}
          onClick={handleSubmit}
          sx={{ mt: 3, py: 1.5 }}
        >
          {isUploading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              <span>Uploading...</span>
            </Box>
          ) : (
            'Create Template'
          )}
        </Button>
      </Paper>
    </Box>
  );
};

